import { EntityBulkGridDialog } from '@/common/entityFramework/bulk/EntityBulkGridDialog';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { useEffect, useMemo, useState } from 'react';

const ROLE_DISPLAY_NAMES = { parent: 'Parent Customer', child: 'Child Customer' };

/**
 * Paste-from-spreadsheet editor for this Customer's relationships.
 *
 * <p>Column headers mirror the CUST_REL sheet of the Cust Rel Resubmittable Excel export, so a sheet
 * produced by that export can be pasted straight in without the file upload round trip. {@code ref}
 * identifies an existing relationship exactly as it does on resubmission; rows without a known ref
 * fall back to matching on the Customer pair and type, and anything left over is added.
 */
export const CustRelBulkDialog = ({ closeDialog, custObj, mode, module }) => {
  const rsrc = useRsrc();
  const [fieldDefs, setFieldDefs] = useState(null);
  const [existingRows, setExistingRows] = useState(null);

  // mode tracks this Customer's side of the relationship, so the other side is what the user supplies
  const fixedRole = mode;
  const fixedField = `${fixedRole}CustCode`;

  useEffect(() => {
    rsrc.fieldDefs(module, 'CUST_REL').then((defs) => setFieldDefs(Object.fromEntries(defs.map((d) => [d.field, d]))));
  }, []);

  useEffect(() => {
    const expr = ExprUtils.whereParams(`${fixedRole}CustId`, custObj.id);
    rsrc.rows(module, 'CUST_REL', { expr }).then((rows) => setExistingRows(rows ?? []));
  }, [custObj.id, fixedRole]);

  const columns = useMemo(() => {
    if (!fieldDefs) return null;

    const custColumn = (role) => ({
      field: `${role}CustCode`,
      header: `${role}_cust_code`,
      displayName: ROLE_DISPLAY_NAMES[role],
      type: 'entity',
      module,
      entity: 'CUST',
      required: true,
      fixed: role === fixedRole,
    });

    return [
      { field: 'ref', header: 'ref', displayName: 'Reference', type: 'text', width: 220 },
      { field: 'code', header: 'code', displayName: 'Code', type: 'text', width: 220 },
      custColumn('parent'),
      custColumn('child'),
      {
        field: 'relType',
        header: 'rel_type',
        displayName: 'Type',
        type: 'enum',
        values: fieldDefs.relType?.values ?? [],
        required: true,
      },
      { field: 'startDate', header: 'start_date', displayName: 'Start Date', type: 'date', required: true, width: 120 },
      { field: 'endDate', header: 'end_date', displayName: 'End Date', type: 'date', required: true, width: 120 },
    ];
  }, [fieldDefs, fixedRole]);

  const existingByRef = useMemo(
    () => Object.fromEntries((existingRows ?? []).filter((row) => row.ref).map((row) => [row.ref, row])),
    [existingRows],
  );

  const relKey = (row) => `${row.parentCustCode}|${row.childCustCode}|${row.relType}`;

  const existingByRelKey = useMemo(
    () => Object.fromEntries((existingRows ?? []).map((row) => [relKey(row), row])),
    [existingRows],
  );

  const existingRow = (row) => existingByRef[row.ref] ?? existingByRelKey[relKey(row)];

  const classifyRow = (row) => (existingRow(row) ? 'UPDATE' : 'NEW');

  const validateRow = (row) => {
    const errors = {};
    if (row.ref && !existingByRef[row.ref]) {
      errors.ref = 'No relationship on this Customer has that reference';
    }
    if (row.parentCustCode && row.parentCustCode === row.childCustCode) {
      errors.childCustCode = 'A Customer cannot be related to itself';
    }
    if (row.startDate && row.endDate && row.startDate > row.endDate) {
      errors.endDate = 'Before the start date';
    }
    return errors;
  };

  const custId = (row, role) => {
    if (role === fixedRole) return custObj.id;
    const column = columns.find((candidate) => candidate.field === `${role}CustCode`);
    return column?.codeToNode?.[row[`${role}CustCode`]]?.id;
  };

  const onSubmit = (rows) => {
    const custRels = rows.map((row) => {
      const existing = existingRow(row);
      const parentCustCode = row.parentCustCode;
      const childCustCode = row.childCustCode;

      return {
        ...(existing ? { id: existing.id } : {}),
        code: row.code || existing?.code || `${parentCustCode}_REL_${childCustCode}`,
        parentCustId: custId(row, 'parent'),
        childCustId: custId(row, 'child'),
        relType: row.relType,
        startDate: row.startDate,
        endDate: row.endDate,
      };
    });

    return rsrc
      .save(module, 'CUST_REL', custRels)
      .then(() => ({ ok: true }))
      .catch((error) => {
        const message = error?.response?.data?.message ?? error?.message ?? 'The save was rejected';
        return { ok: false, errors: rows.map(({ rowIdx }) => ({ rowIdx, field: '_row', message })) };
      });
  };

  // Existing relationships in the same layout so users can copy, adjust in Excel, and paste back
  const exportRows = useMemo(
    () =>
      (existingRows ?? []).map((row) => ({
        ref: row.ref,
        code: row.code,
        parentCustCode: row.parentCustCode,
        childCustCode: row.childCustCode,
        relType: row.relType,
        startDate: row.startDate,
        endDate: row.endDate,
      })),
    [existingRows],
  );

  if (!columns || !existingRows) return null;

  return (
    <EntityBulkGridDialog
      closeDialog={closeDialog}
      title={`Paste Relationships — ${custObj.code}`}
      subtitle={
        `Rows with a known reference (or the same Customer pair and type) update; everything else is added. ` +
        `${ROLE_DISPLAY_NAMES[fixedRole]} is pinned to this Customer.`
      }
      columns={columns}
      fixedValues={{ [fixedField]: custObj.code }}
      classifyRow={classifyRow}
      validateRow={validateRow}
      onSubmit={onSubmit}
      exportRows={exportRows}
    />
  );
};
